import React, { useEffect, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L, { LatLngBounds } from 'leaflet';
import '@maplibre/maplibre-gl-leaflet';
import { MapContainer, Pane, Polygon } from 'react-leaflet';
import { FullscreenControl } from 'react-leaflet-fullscreen';
import 'react-leaflet-fullscreen/styles.css';
import { MarkerData } from './LandscapeMapTypes';
import Switch from 'react-switch';
import Control from 'react-leaflet-custom-control';
import BreathingMarker from './Markers/Animation/BreathingMarker';
import SimpleMarker from './Markers/Animation/SimpleMarker';
import ConvertGeoJsonToPolylineData from './Utils/convertGeoJsonToPolylineData';
import usBoundaries from './geoBoundaries-USA-ADM1_simplified.geo.json';
import MapLegend from './MapLegend/MapLegend';
import 'react-leaflet-fullscreen/styles.css';

export interface LandscapeMapProps {
    mapStylesUrl: string;
    bounds: LatLngBounds;
    minZoom: number;
    maxZoom: number;
    defaultZoom?: number;
    currentZoom?: number;
    setCurrentZoom: (zoom: number) => void;
    markerVisibilityZoom: number;
    markers?: MarkerData[];
    selectedMarkers: MarkerData[];
    isFullscreen?: boolean;
    setIsFullscreen?: (value: boolean) => void;
    isSubMap: boolean;
    subMaps?: () => React.JSX.Element[];
    markersSize?: number;
    renderColorsDescription?: () => React.JSX.Element[];
    renderShapesDescription?: () => React.JSX.Element[];
}

export const isWebGLAvailable = (): boolean => {
    try {
        const canvas = document.createElement('canvas');
        return !!(
            canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl')
        );
    } catch {
        return false;
    }
};

const LandscapeMap = (props: LandscapeMapProps) => {
    const defaultMarkerSizeScale = 12;
    const defaultIdNdaSignedLimitZoom = 6;
    const highZoomMinRadius = 600;
    const [webGLAvailable] = useState(() => isWebGLAvailable());
    const [mapRef, setMapRef] = useState<L.Map | null>(null);
    const [mapPolyline, setMapPolyline] = useState<any[]>([]);
    const [areExistingPracticesVisible, setAreExistingPracticesVisible] =
        useState(true);
    // Define a large outer polygon that covers the entire map
    const outerBounds: [number, number][] = [
        [90, -350],
        [90, 250],
        [-90, 250],
        [-90, -350],
        [90, -350],
    ];

    useEffect(() => {
        if (!mapRef) return;
        L.maplibreGL({
            style: props.mapStylesUrl,
        }).addTo(mapRef);

        if (usBoundaries) {
            const res = ConvertGeoJsonToPolylineData.runConvertor(usBoundaries);
            setMapPolyline(res);
        }

        mapRef.on('zoomend', (e: any) => props.setCurrentZoom(e.target._zoom));
    }, [mapRef]);

    useEffect(() => {
        props.setCurrentZoom(props.defaultZoom ?? props.minZoom);
    }, []);

    useEffect(() => {
        if (!mapRef) return;
        const handleEnterFullscreen = () => {
            props.setIsFullscreen?.(true);
            setTimeout(() => mapRef.invalidateSize(), 100);
        };
        const handleExitFullscreen = () => {
            props.setIsFullscreen?.(false);
            setTimeout(() => mapRef.invalidateSize(), 100);
        };
        mapRef.on('enterFullscreen', handleEnterFullscreen);
        mapRef.on('exitFullscreen', handleExitFullscreen);
        return () => {
            mapRef.off('enterFullscreen', handleEnterFullscreen);
            mapRef.off('exitFullscreen', handleExitFullscreen);
        };
    }, [mapRef]);

    const getScaledRadius = (
        minRadius: number,
        maxRadius: number,
        maxFteDVMs: number
    ): Record<number, number> => {
        const radius: Record<number, number> = {};

        const rawValues: number[] = [];
        for (let x = 1; x <= maxFteDVMs; x++) {
            const raw = Math.pow(99 * x - 1, 0.56) * 100;
            rawValues.push(raw);
        }

        const minRaw = rawValues[0];
        const maxRaw = rawValues[maxFteDVMs - 1];

        for (let i = 0; i < maxFteDVMs; i++) {
            const normalized =
                minRadius +
                ((rawValues[i] - minRaw) / (maxRaw - minRaw)) *
                    (maxRadius - minRadius);
            radius[i + 1] = Math.round(normalized * 100) / 100;
        }

        return radius;
    };

    const getCircleRadius = (
        fteDVMs: number,
        zoom: number,
        latitude: number
    ): number => {
        if (props.markersSize) {
            return props.markersSize;
        }

        const maxRadius = 150168;
        const minRadius = 35000;
        const maxFteDVMs = 50;
        const fteNumber = Math.min(
            Math.max(Math.round(fteDVMs), 1),
            maxFteDVMs
        );

        const hardcodedRadii = getScaledRadius(
            minRadius,
            maxRadius,
            maxFteDVMs
        );
        const baseRadius = hardcodedRadii[fteNumber];

        const latitudeAdjustedRadius = adjustRadiusForLatitude(
            baseRadius,
            latitude
        );

        // Apply zoom adjustment as per your logic
        const zoomFactor = Math.pow(2, props.maxZoom - zoom) * (zoom / 2);
        const radiusScale = defaultMarkerSizeScale * zoomFactor;
        const etalonMarkerSize = (175 * radiusScale) / 100;

        // Adapt radius proportionally to zoom
        const adjustedRadius =
            (latitudeAdjustedRadius / maxRadius) * etalonMarkerSize;

        const result = Math.max(
            highZoomMinRadius,
            Math.min(adjustedRadius, maxRadius)
        );

        return result;
    };

    const adjustRadiusForLatitude = (
        radius: number,
        latitude: number
    ): number => {
        return radius * Math.cos(latitude * (Math.PI / 180));
    };

    if (!webGLAvailable) return null;

    return (
        <MapContainer
            zoomDelta={0.5}
            zoomSnap={0.5}
            ref={setMapRef}
            bounds={props.bounds}
            zoom={props.defaultZoom}
            minZoom={props.minZoom}
            maxZoom={props.maxZoom}
            maxBounds={props.bounds}
            maxBoundsViscosity={1}
            preferCanvas={true}
            style={{ height: props.isSubMap ? '60px' : '50vh' }}
            renderer={L.canvas({ padding: 2 })}
            zoomControl={props.isSubMap ? false : true}
            dragging={props.isSubMap ? false : true}
            scrollWheelZoom={props.isSubMap ? false : true}
            doubleClickZoom={props.isSubMap ? false : true}>
            <Pane name="markers-pane" style={{ zIndex: 101 }}>
                {props.markers
                    ?.filter((marker) => {
                        if (
                            !marker?.isIdNdaSigned &&
                            props.currentZoom >= defaultIdNdaSignedLimitZoom
                        ) {
                            return false;
                        }

                        return (
                            marker?.color !== 'OurExistingPractices' ||
                            (marker?.color === 'OurExistingPractices' &&
                                areExistingPracticesVisible)
                        );
                    })
                    .map((marker, index) => {
                        const radius = getCircleRadius(
                            marker.fteDVMs,
                            props.currentZoom,
                            marker.position[0]
                        );

                        const isSelected = props.selectedMarkers.some(
                            (x) => x.id === marker.id
                        );

                        return props.currentZoom <=
                            props.markerVisibilityZoom ? (
                            isSelected ? (
                                <BreathingMarker
                                    onClick={marker.onClick}
                                    key={`practice-circle-${index}-${marker.color}`}
                                    color={marker.color}
                                    position={marker.position}
                                    radius={radius}
                                    name={marker.name}
                                    shape={marker.shape}
                                    fteDVMs={marker.fteDVMs}
                                    showTooltip={marker.showTooltip}
                                />
                            ) : (
                                <SimpleMarker
                                    onClick={marker.onClick}
                                    key={`practice-circle-${index}-${marker.color}`}
                                    shape={marker.shape}
                                    color={marker.color}
                                    position={marker.position}
                                    radius={radius}
                                    name={marker.name}
                                    fteDVMs={marker.fteDVMs}
                                    showTooltip={marker.showTooltip}
                                />
                            )
                        ) : (
                            <></>
                        );
                    }) ?? <></>}
            </Pane>

            {!props.isSubMap && (
                <>
                    <FullscreenControl forcePseudoFullscreen={true} />
                    <MapLegend
                        renderColorsDescription={props.renderColorsDescription}
                        renderShapesDescription={props.renderShapesDescription}
                    />
                    {/* 
                    <Control position="topright">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}>
                            <Switch
                                offColor="#d92550"
                                checked={areExistingPracticesVisible}
                                onChange={() =>
                                    setAreExistingPracticesVisible(
                                        !areExistingPracticesVisible
                                    )
                                }
                                height={22}
                            />
                            <div>Include Existing Practices</div>
                        </div>
                    </Control>
                    */}
                    {props.subMaps && props.subMaps()}
                </>
            )}
            <Polygon
                pane="overlayPane"
                positions={[outerBounds, ...mapPolyline]}
                color="transparent"
                fillColor="#dddddd"
                fillOpacity={1}
            />
        </MapContainer>
    );
};

export default LandscapeMap;
